import { useEffect, useState } from "react";
import { listPendingEdits, testInSandbox, type PriceEditProposal } from "./actions";

// Review surface for ops/PRODUCT_EDIT_WORKFLOW_SCOPE.md's proposal queue.
// "Test in sandbox" applies a proposal's final price to the LOCAL hec_sandbox
// DB only (ISSUES.md #22) -- never production, never even InMotion. Only
// works when backend/api/main.py runs on a machine where hec_sandbox exists.
// Production promotion is NOT built here, explicitly gated on ISSUES.md
// #20's still-deferred write-credential decision.

function money(n: number | null) {
  return n == null ? "—" : n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function PendingEditsPanel() {
  const [rows, setRows] = useState<PriceEditProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; message: string; ok: boolean } | null>(null);

  const load = () => {
    setLoading(true);
    listPendingEdits().then((r) => {
      setLoading(false);
      if (r.ok) setRows(r.data.rows); else setError(r.error);
    });
  };

  useEffect(load, []);

  const runStagingTest = async (proposalId: string) => {
    setTestingId(proposalId);
    setTestResult(null);
    const r = await testInSandbox(proposalId);
    setTestingId(null);
    if (!r.ok) {
      setTestResult({ id: proposalId, ok: false, message: r.error });
      return;
    }
    setTestResult({
      id: proposalId, ok: true,
      message: `Applied to sandbox post ${r.data.stagingPostId} — ${r.data.rowsUpdated} row(s) updated, readback: ${JSON.stringify(r.data.readback)}`,
    });
    load();
  };

  return (
    <div>
      <h1>Pending edits</h1>
      <p className="meta">
        Proposals only — nothing here has been applied to the corpus, WooCommerce
        staging, or production. See <code>ops/PRODUCT_EDIT_WORKFLOW_SCOPE.md</code> for
        the full review-then-stage-then-promote workflow (promotion to production is
        explicitly not built, gated on a separate write-credential decision).
      </p>

      <button onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
      {error && <div className="panel error">Error: {error}</div>}

      <div className="manual-table" style={{ marginTop: 12 }}>
        <table className="manual-products">
          <thead>
            <tr>
              <th>SKU</th><th>Source</th><th>From</th><th>Proposed</th><th>Final</th>
              <th>MAP clamped</th><th>Status</th><th>Proposed at</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.proposalId} className={p.duplicateGroupBlocked ? "edited" : ""}>
                <td className="mono">{p.sku}</td>
                <td>{p.sourceId}</td>
                <td>{money(p.priceFrom)}</td>
                <td>{money(p.priceProposed)}</td>
                <td>{money(p.priceFinal)}</td>
                <td>{p.priceMapClamped ? `yes (floor $${p.mapPriceAtProposal})` : "no"}</td>
                <td>{p.status}</td>
                <td>{p.proposedAt}</td>
                <td>
                  <button
                    disabled={p.duplicateGroupBlocked || p.status === "tested_in_staging" || testingId === p.proposalId}
                    onClick={() => runStagingTest(p.proposalId)}
                  >
                    {testingId === p.proposalId ? "Testing…"
                      : p.status === "tested_in_staging" ? "Tested"
                      : "Test in sandbox"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && !loading && <p className="meta" style={{ padding: 12 }}>No pending edits proposed yet.</p>}
      </div>

      {testResult && (
        <div className={`panel ${testResult.ok ? "" : "error"}`} style={{ marginTop: 12 }}>
          {testResult.ok ? "✅ " : "❌ "}{testResult.message}
        </div>
      )}
    </div>
  );
}
